import { useEffect, useRef, useState } from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { TrendingUp, Target, Zap, BarChart3 } from "lucide-react";
import { cn } from "@/lib/utils";

const AboutSection = () => {
  const ref = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(element);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const highlights = [
    {
      icon: TrendingUp,
      title: "Growth Driver",
      description: "65%+ YoY growth in qualified leads"
    },
    {
      icon: Target,
      title: "Conversion Expert",
      description: "Improved rates from 1.9% to 3.4%"
    },
    {
      icon: BarChart3,
      title: "Cost Optimizer",
      description: "Reduced CAC by up to 28%"
    },
    {
      icon: Zap,
      title: "AI-Powered",
      description: "Scalable marketing systems"
    }
  ];

  return (
    <section 
      id="about" 
      ref={ref}
      className={`py-12 sm:py-16 md:py-20 border-t border-border scroll-animate ${isVisible ? 'visible' : ''}`}
    >
      <AccordionPrimitive.Root type="single" collapsible className="w-full">
        <AccordionPrimitive.Item value="about" className="border-none">
          <AccordionPrimitive.Header className="flex" asChild={false}>
            <h2>
              <AccordionPrimitive.Trigger
                className={cn(
                  "group flex items-center gap-4 py-2",
                  "text-foreground hover:text-primary",
                  "transition-all duration-500 ease-out",
                  "focus:outline-none"
                )}
              >
                {/* Animated line */}
                <span className="relative h-[2px] w-8 sm:w-12 bg-muted-foreground/30 overflow-hidden group-hover:bg-primary/30 transition-colors duration-300">
                  <span className="absolute inset-0 bg-primary transform -translate-x-full group-hover:translate-x-0 group-data-[state=open]:translate-x-0 transition-transform duration-500 ease-out" />
                </span>
                
                {/* Text with elegant styling - 1.5x bigger and bold on mobile */}
                <span className="text-4xl sm:text-3xl md:text-3xl font-display font-bold sm:font-light tracking-wide text-primary">
                  About Me
                </span>
                
                {/* Rotating plus/minus icon */}
                <span className="relative w-6 h-6 sm:w-6 sm:h-6 flex items-center justify-center">
                  <span className="absolute h-[2px] w-full bg-current transition-all duration-300" />
                  <span className="absolute h-[2px] w-full bg-current rotate-90 group-data-[state=open]:rotate-0 transition-transform duration-300" />
                </span>
              </AccordionPrimitive.Trigger>
            </h2>
          </AccordionPrimitive.Header>
          <AccordionPrimitive.Content
            className={cn(
              "overflow-hidden",
              "data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
            )}
          >
            <div className="pt-6 sm:pt-8 max-w-4xl">
              {/* Article-style Content - No nested scrolling */}
              <div className="p-4 sm:p-6 space-y-6 sm:space-y-8 rounded-xl border border-border/50 bg-card/30 backdrop-blur-sm">
                {/* Hero Statement - 1.3x bigger on mobile */}
                <div className="border-l-4 border-primary pl-4 sm:pl-6">
                  <p className="text-[1.625rem] sm:text-xl md:text-2xl lg:text-3xl font-medium text-foreground leading-relaxed">
                    Product-oriented senior marketing specialist with <span className="text-primary font-bold">7+ years</span> of experience.
                  </p>
                </div>

                {/* Key Metrics Grid */}
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  {highlights.map((item, index) => (
                    <div 
                      key={index}
                      className="p-3 sm:p-4 md:p-5 rounded-xl bg-muted/50 border border-border/50 hover:border-primary/30 transition-colors"
                    >
                      <item.icon className="h-7 w-7 sm:h-7 sm:w-7 text-primary mb-2" />
                      {/* 1.3x bigger text on mobile */}
                      <h4 className="font-semibold text-foreground text-xl sm:text-lg">{item.title}</h4>
                      <p className="text-[1.125rem] sm:text-base text-muted-foreground">{item.description}</p>
                    </div>
                  ))}
                </div>

                {/* Expertise Areas */}
                <div className="space-y-3 sm:space-y-4">
                  <h3 className="text-base sm:text-base uppercase tracking-wider text-muted-foreground font-semibold">
                    Core Expertise
                  </h3>
                  <div className="flex flex-wrap gap-2 sm:gap-3">
                    {["Full-funnel Campaigns", "Product Launches", "Brand Transformation", "GTM Strategy", "Performance Ads", "CRO"].map((skill) => (
                      <span 
                        key={skill}
                        className="px-3 sm:px-4 py-1.5 sm:py-2 text-[1.125rem] sm:text-base rounded-full bg-primary/10 text-primary border border-primary/20"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Approach */}
                <div className="space-y-3 sm:space-y-4">
                  <h3 className="text-base sm:text-base uppercase tracking-wider text-muted-foreground font-semibold">
                    My Approach
                  </h3>
                  {/* 1.3x bigger on mobile */}
                  <p className="text-xl sm:text-lg text-muted-foreground leading-relaxed">
                    I combine <span className="text-foreground font-medium">analytics, creativity, and AI-powered tools</span> to build scalable marketing systems. From product positioning to multi-channel execution, I focus on <span className="text-foreground font-medium">measurable impact and sustainable growth</span>.
                  </p>
                </div>

                {/* Channels */}
                <div className="space-y-3 sm:space-y-4">
                  <h3 className="text-base sm:text-base uppercase tracking-wider text-muted-foreground font-semibold">
                    Channels & Tactics
                  </h3>
                  <div className="flex flex-wrap gap-2 sm:gap-3">
                    {["Paid Acquisition", "Content Marketing", "Influencer Marketing", "OOH", "Email", "Social Media"].map((channel) => (
                      <span 
                        key={channel}
                        className="px-3 sm:px-4 py-1.5 sm:py-2 text-[1.125rem] sm:text-base rounded-full bg-muted text-muted-foreground border border-border"
                      >
                        {channel}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </AccordionPrimitive.Content>
        </AccordionPrimitive.Item>
      </AccordionPrimitive.Root>
    </section>
  );
};

export default AboutSection;
